import React, { useMemo } from "react";
import { Text, Badge, Group } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";
import { LookupDictionary } from "@/utils/api";
import { formatDateTime } from "@/utils/dateHelpers";

export interface LookupDictionaryTableProps {
  /** Array of lookup dictionaries to display */
  lookupDictionaries: LookupDictionary[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditLookupDictionary?: (lookupDictionary: LookupDictionary) => void;

  /** Callback when remove action is clicked */
  onRemoveLookupDictionary?: (lookupDictionary: LookupDictionary) => void;

  /** Callback when go in (view items) action is clicked */
  onGoInLookupDictionary?: (lookupDictionary: LookupDictionary) => void;

  /** ID of lookup dictionary currently being removed */
  removingLookupDictionaryId?: string | null;

  /** ID of lookup dictionary just edited or added */
  editedItemId?: string | null | undefined;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const LookupDictionaryTable: React.FC<LookupDictionaryTableProps> = ({
  lookupDictionaries,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditLookupDictionary,
  onRemoveLookupDictionary,
  onGoInLookupDictionary,
  removingLookupDictionaryId,
  editedItemId,
  size = "md",
}) => {
  const columns: AppTableColumn<LookupDictionary>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Name & Description",
        sortable: true,
        flex: true,
        minWidth: 250,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value || "Unnamed dictionary"}</Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "externalUrl",
        label: "External URL",
        sortable: true,
        width: 200,
        minWidth: 150,
        render: (value) => {
          if (!value) {
            return (
              <Badge variant="light" color="gray" size="sm">
                Local
              </Badge>
            );
          }
          return (
            <Text size="sm" c="blue" lineClamp={1} title={value}>
              {value}
            </Text>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 100,
        align: "left",
        render: (value) => <StatusBadge status={value} />,
      },
      {
        key: "createdAt",
        label: "Created",
        sortable: true,
        width: 150,
        minWidth: 150,
        align: "left",
        render: (value) => (
          <Text
            size="sm"
            c="dimmed"
            dangerouslySetInnerHTML={{
              __html: formatDateTime(value, "datetime2"),
            }}
          ></Text>
        ),
      },
      {
        key: "actions",
        label: "",
        width: 180,
        minWidth: 180,
        align: "left",
        render: (_, record: LookupDictionary) => {
          const isRemoving = removingLookupDictionaryId === record.id;

          return (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              <RecordActions
                context={record}
                onEditClick={onEditLookupDictionary}
                onRemoveClick={onRemoveLookupDictionary}
                onGoInClick={onGoInLookupDictionary}
                goInTooltip="Manage dictionary items"
                removeInProgress={isRemoving}
              />
            </Group>
          );
        },
      },
    ],
    [
      onEditLookupDictionary,
      onRemoveLookupDictionary,
      onGoInLookupDictionary,
      removingLookupDictionaryId,
    ]
  );

  return (
    <AppTable
      data={lookupDictionaries}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(lookupDictionary) => lookupDictionary?.id || ""}
      highlightRow={(lookupDictionary) => lookupDictionary?.id === editedItemId}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
